<template>
  <IntegrationStatusHeader
    :auth-by="salesforceAuthUsername"
    :disabled="isMissingSFDCConnection"
    @reauthorize="reauthorize"
  />
  <div v-if="profileType" class="flex flex-col">
    <div v-if="hasPermissionToManageIntegrations" class="p-16 h-full">
      <SFDCMissingConnectionEmptyState v-if="isMissingSFDCConnection" />
      <template v-else>
        <div class="flex items-center gap-8 mb-4">
          <FontAwesomeIcon
            :icon="['fak', 'mapping']"
            class="text-secondary-text"
          />
          <p class="font-bold"> Customize Data in Push </p>
        </div>
        <p class="text-neutral-text text-sm">
          {{ integrationDescription }}
          <BittsLink
            :url="trayState.enabledTray?.learnMore"
            text="Learn more about this"
            :open-in-new-tab="true"
          />
        </p>
        <IntegrationSettings
          v-if="integrationProfileSettings && trayState.profileType"
          :population-settings="{
            population_settings:
              integrationProfileSettings?.population_settings ?? [],
            standard_population_settings:
              integrationProfileSettings?.standard_population_settings ?? [],
            exclude_partnerships_created_since:
              integrationProfileSettings?.exclude_partnerships_created_since,
          }"
          :setting-type="trayState.profileType"
          @save:changes="onSettingsUpdated"
          @update:exclude-partnerships-created-since="
            onUpdateExcludePartnershipsCreatedSince
          "
          :is-disabled="
            !isIntegrationAvailable ||
            trayState.isSaving ||
            loading ||
            isLoading
          "
        />
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { BittsLink } from '@crossbeam/bitts';

import { computed, inject, ref, watchEffect } from 'vue';

import { crossbeamApi } from '@/api';
import useAuth from '@/composables/useAuth';
import useIntegrationProfile from '@/composables/useIntegrationProfile';
import useTrayIntegrations from '@/composables/useTrayIntegrations';
import useTrayStatus from '@/composables/useTrayStatus';
import { captureException } from '@/errors';
import { ProvidedTrayState } from '@/injectionKeys/integration_profiles';
import { useFlashesStore, usePartnerCloudStore } from '@/stores';
import { IntegrationProfileType } from '@/types/integration_profiles';

import IntegrationSettings from './IntegrationSettings.vue';
import IntegrationStatusHeader from './IntegrationStatusHeader.vue';
import SFDCMissingConnectionEmptyState from './SFDCMissingConnectionEmptyState.vue';

const emit = defineEmits<{
  'remove-integration': [];
  'close-drawer': [];
  'changed:integration-profile': [changed: boolean];
}>();
const loading = ref(false);
// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
const trayState = inject(ProvidedTrayState)!;

const partnerCloudStore = usePartnerCloudStore();

const enabledTray = computed(() => trayState.enabledTray);

const {
  hasPermissionToManageIntegrations,
  integrationDescription,
  integrationProfileSettings,
  isIntegrationAvailable,
  isLoading,
  onSettingsUpdated,
  onUpdateExcludePartnershipsCreatedSince,
  saveIntegrationProfile,
  isSettingDirty,
} = useIntegrationProfile(trayState.profileType);

const { hasSalesforceConnection } = useTrayStatus(enabledTray);

const profileType = computed(() => trayState.profileType);

const isMissingSFDCConnection = computed(() => {
  return profileType.value === 'salesforce' && !hasSalesforceConnection?.value;
});

const salesforceAuthUsername = computed(() => {
  if (profileType.value !== 'salesforce') return undefined;
  const salesforceAuth = enabledTray.value?.authValues?.find(
    (auth) => auth.externalId === 'external_salesforce_authentication',
  );
  return salesforceAuth?.username;
});

const { addErrorFlash } = useFlashesStore();
const { currentOrg } = useAuth();

const reauthorize = async () => {
  if (!enabledTray?.value) return;

  const { openConfigWindow } = useTrayIntegrations();
  try {
    loading.value = true;
    const body = {
      org_name: currentOrg.value.name,
      instance_name: enabledTray.value?.name,
      solution_id: enabledTray.value?.solution.id,
      instance_id: enabledTray.value?.id,
    };
    const { data, error } = await crossbeamApi.PATCH(
      '/v0.1/tray-integrations',
      {
        body,
      },
    );

    if (error) throw new Error(error);

    openConfigWindow(data);
  } catch (err) {
    addErrorFlash({
      message: 'Something went wrong',
      description: `If this error persists, please contact
      <a target="_blank" href="mailto:support@crossbeam.com">support@crossbeam.com</a>.`,
    });
    captureException(err);
  } finally {
    loading.value = false;
  }
};

const VALID_PROFILES: IntegrationProfileType[] = [
  'salesforce',
  'hubspot',
  'microsoft_dynamics',
];

async function onSaveGeneralSettings() {
  const type = profileType.value;

  if (!type || !VALID_PROFILES.includes(type)) {
    emit('close-drawer');
    return;
  }

  trayState.isSaving = true;

  const [, trayConfigSuccess] = await Promise.all([
    saveIntegrationProfile(),
    trayState.hasTrayConfigChanges
      ? trayState.saveTrayConfig()
      : Promise.resolve(true),
  ]);

  trayState.isSaving = false;
  isSettingDirty.value = false;
  trayState.isProfileSettingDirty = false;

  if (trayConfigSuccess) {
    partnerCloudStore.$patch({ inProgressInstallation: null });
  }
}

watchEffect(() => {
  if (isSettingDirty.value) {
    emit('changed:integration-profile', true);
  }
});

defineExpose({
  onSaveGeneralSettings,
});
</script>
